import { Directive, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Locks the browser back / forward / side-button / Alt+Arrow navigation while
 * the host element is alive. Apply on the root element of any screen that must
 * not be navigable via browser history — e.g. login, onboarding entry, terminal
 * error pages.
 *
 *   <div appNoBackNav> ... </div>
 *
 * Mechanism: push a duplicate history entry on init, and on every popstate
 * push another duplicate. The URL never changes, so Angular Router sees no
 * diff and won't navigate. When the directive is destroyed (user successfully
 * navigates away through an in-app action) the listener is removed so the
 * next screen's history works normally.
 */
@Directive({
  selector: '[appNoBackNav]',
  standalone: true
})
export class NoBackNav implements OnInit, OnDestroy {
  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;
    // Duplicate the current entry so back/forward have somewhere to land
    // that resolves to the same URL.
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', this.onPopState);
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) return;
    window.removeEventListener('popstate', this.onPopState);
  }

  private readonly onPopState = (): void => {
    // Re-push so the cursor stays on a duplicate of the current URL.
    // Works under HashBangLocationStrategy because location.href already
    // contains the full hash-bang URL — no special handling needed.
    history.pushState(null, '', window.location.href);
  };
}
